<!-- Template -->
<template>
  <div
    class="flex items-center justify-between gap-4 mb-4 p-4 bg-white rounded-lg shadow-sm flex-wrap md:flex-nowrap"
  >
    <!-- Groupe Gauche: Tri & Type -->
    <div class="flex items-center gap-4 flex-shrink-0">
      <!-- Menu Tri -->
      <div class="relative inline-flex items-center">
        <div class="relative">
          <button
            class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:border-gray-800 transition-colors duration-200 min-w-[140px]"
            @click="toggleSortMenu"
          >
            <ArrowUpDown
              class="h-4 w-4 text-gray-500 hover:text-gray-800 hover:cursor-pointer"
              :class="{ 'transform rotate-180': sortDirection === 'desc' }"
              @click.stop="toggleSortDirection"
            />
            <span>{{ sortByLabel }}</span>
            <ChevronDown
              class="h-4 w-4 text-gray-400 ml-auto transition-transform duration-200"
              :class="{ 'transform rotate-180': showSortMenu }"
            />
          </button>

          <!-- Menu déroulant -->
          <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <div
              v-if="showSortMenu"
              class="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg py-1 z-50"
            >
              <button
                v-for="option in sortOptions"
                :key="option.value"
                @click="selectSortOption(option.value)"
                class="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700"
                :class="{ 'bg-gray-50': sortBy === option.value }"
              >
                {{ option.label }}
              </button>
            </div>
          </transition>
        </div>
      </div>
      <!-- Bouton Type (Tri-state) -->
      <button
        @click="cycleTypeFilter"
        class="flex items-center gap-1.5 px-3 py-2 bg-white border rounded-md text-sm transition-colors duration-200 flex-shrink-0"
        :class="[
          typeFilter !== 'all'
            ? typeFilter === 'figure'
              ? 'text-blue-800 bg-blue-50'
              : 'text-green-800 bg-green-50'
            : 'text-gray-600 hover:bg-gray-50',
        ]"
        :title="`Filtrer par type: ${typeFilterLabel}`"
      >
        <component :is="typeFilterIcon" class="h-4 w-4" />
        <span class="hidden sm:inline">{{ typeFilterLabel }}</span>
      </button>

      <!-- Bouton Mur (Tri-state) -->
      <button
        @click="cycleWallFilter"
        class="flex items-center gap-1.5 px-3 py-2 bg-white border rounded-md text-sm transition-colors duration-200 flex-shrink-0"
        :class="[
          murFilter !== 'all'
            ? murFilter === 'Nord'
              ? 'text-orange-800 bg-orange-50'
              : 'text-purple-800 bg-purple-50'
            : 'text-gray-600 hover:bg-gray-50',
        ]"
        :title="`Filtrer par mur: ${wallFilterLabel}`"
      >
        <component :is="wallFilterIcon" class="h-4 w-4" />
        <span class="hidden sm:inline">{{ wallFilterLabel }}</span>
      </button>
    </div>

    <!-- Groupe Central: Recherche & Filtres avancés -->
    <div class="flex items-center gap-4 flex-grow min-w-0">
      <!-- Barre de recherche (prend plus de place) -->
      <div class="relative flex-grow min-w-[150px] sm:min-w-[200px]">
        <Search
          class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
        />
        <input
          type="text"
          v-model="searchQuery"
          @input="debouncedEmitFilters"
          placeholder="Rechercher..."
          class="w-full pl-10 pr-4 py-2 bg-white border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <!-- Dropdown Filtres -->
      <div class="relative flex-shrink-0">
        <button
          @click="toggleDropdown"
          class="flex items-center gap-1.5 px-3 py-2 bg-white border rounded-md text-sm transition-colors duration-200"
          :class="[
            hasActiveDropdownFilters
              ? 'text-primary bg-primary bg-opacity-10'
              : 'text-gray-600 hover:bg-gray-50',
          ]"
        >
          <SlidersHorizontal class="h-4 w-4" />
          <span class="hidden sm:inline">Filtres</span>
          <ChevronDown
            class="h-4 w-4 transition-transform duration-200"
            :class="{ 'transform rotate-180': showDropdown }"
          />
        </button>
        <!-- Menu Dropdown -->
        <transition name="fade">
          <div
            v-if="showDropdown"
            class="absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-20 p-4 space-y-4"
            @click.stop
          >
            <!-- Visibilité -->
            <div class="space-y-2">
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  v-model="visibleState"
                  @change="emitFilters"
                  class="rounded text-primary focus:ring-primary"
                />
                <span class="text-sm">Visible</span>
              </label>
            </div>

            <!-- Relevés -->
            <div class="space-y-2 pt-2 border-t">
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-500">Relevés</span>
                <span class="text-xs text-gray-400 italic">Cocher pour filtrer</span>
              </div>
              <div class="space-y-2">
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="relevesState.includes('2017')"
                    @change="toggleReleve('2017')"
                    class="rounded text-primary focus:ring-primary"
                  />
                  <span class="text-sm">2017</span>
                </label>
                <label class="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="relevesState.includes('2022')"
                    @change="toggleReleve('2022')"
                    class="rounded text-primary focus:ring-primary"
                  />
                  <span class="text-sm">2022</span>
                </label>
              </div>
              <p class="text-xs text-gray-400 mt-1">
                Aucune sélection ou les deux cochées = afficher tout
              </p>
            </div>

            <!-- Langues -->
            <div class="space-y-2 pt-2 border-t">
              <span class="text-sm font-medium text-gray-500">Langues</span>
              <div class="space-y-2">
                <label
                  v-for="(langue, index) of filteredLangues"
                  :key="index"
                  class="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    v-model="languesState"
                    :value="langue"
                    @change="emitFilters"
                    class="rounded text-primary focus:ring-primary"
                  />
                  <span class="text-sm">{{ langue }}</span>
                </label>
              </div>
            </div>

            <!-- Catégories (conditionnel) -->
            <div v-if="props.showCategoriesFilter" class="space-y-2 pt-2 border-t">
              <span class="text-sm font-medium text-gray-500">Catégories</span>
              <div class="space-y-2">
                <label
                  v-for="category in categoriesList"
                  :key="category"
                  class="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    v-model="categoriesState"
                    :value="category"
                    @change="emitFilters"
                    class="rounded text-primary focus:ring-primary"
                  />
                  <span class="text-sm">{{ category }}</span>
                </label>
              </div>
            </div>

            <!-- Thématiques (conditionnel) -->
            <div v-if="props.showThematiqueFilter" class="space-y-2 pt-2 border-t">
              <span class="text-sm font-medium text-gray-500">Thématiques</span>
              <div class="space-y-2">
                <label
                  v-for="thematique in thematiquesList"
                  :key="thematique"
                  class="flex items-center space-x-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    v-model="thematiqueState"
                    :value="thematique"
                    @change="emitFilters"
                    class="rounded text-primary focus:ring-primary"
                  />
                  <span class="text-sm">{{ thematique }}</span>
                </label>
              </div>
            </div>

            <!-- Bouton Réinitialiser -->
            <button
              @click="resetDropdownFilters"
              class="w-full mt-4 text-sm text-center text-gray-500 hover:text-primary transition-colors duration-200"
              :disabled="!hasActiveDropdownFilters"
              :class="{ 'opacity-50 cursor-not-allowed': !hasActiveDropdownFilters }"
            >
              Réinitialiser les filtres
            </button>
          </div>
        </transition>
      </div>
    </div>

    <!-- Groupe Droite: Vues & Export -->
    <div class="flex items-center gap-4 flex-shrink-0">
      <!-- Boutons vue -->
      <div class="bg-gray-100 rounded-full p-1 flex items-center">
        <button
          @click="setViewMode('card')"
          class="p-1.5 rounded-full transition-colors duration-200"
          :class="[
            viewMode === 'card'
              ? 'bg-white shadow text-primary'
              : 'text-gray-600 hover:text-primary',
          ]"
          title="Vue Grille"
        >
          <LayoutGrid class="h-4 w-4" />
        </button>
        <button
          @click="setViewMode('list')"
          class="p-1.5 rounded-full transition-colors duration-200"
          :class="[
            viewMode === 'list'
              ? 'bg-white shadow text-primary'
              : 'text-gray-600 hover:text-primary',
          ]"
          title="Vue Liste"
        >
          <List class="h-4 w-4" />
        </button>
        <button
          @click="setViewMode('table')"
          class="p-1.5 rounded-full transition-colors duration-200"
          :class="[
            viewMode === 'table'
              ? 'bg-white shadow text-primary'
              : 'text-gray-600 hover:text-primary',
          ]"
          title="Vue Tableau"
        >
          <Table class="h-4 w-4" />
        </button>
      </div>
      <!-- Bouton export -->
      <button
        @click="exportData"
        class="p-2 bg-white border rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200 flex-shrink-0"
        title="Exporter les données affichées (CSV)"
      >
        <Download class="h-4 w-4" />
      </button>
    </div>

    <!-- Ancien menu mobile (simplifié/supprimé pour l'instant) -->
  </div>
</template>

<script setup lang="ts">
import { cacheService } from '@/services/cacheService'
import {
  ArrowLeftToLine,
  ArrowRightLeft,
  ArrowRightToLine,
  ArrowUpDown,
  ChevronDown,
  Download,
  Filter,
  ImageIcon,
  LayoutGrid,
  List,
  Search,
  SlidersHorizontal,
  Table,
  Type,
} from 'lucide-vue-next'
import { computed, onMounted, onUnmounted, ref } from 'vue'

// Props
const props = withDefaults(
  defineProps<{
    showCategoriesFilter?: boolean
    showThematiqueFilter?: boolean
  }>(),
  {
    showCategoriesFilter: true,
    showThematiqueFilter: true,
  }
)

// Define emits with updated types
const emit = defineEmits<{
  (e: 'update:filters', filters: Filters): void
  (e: 'update:view', mode: ViewMode): void
  (e: 'export'): void
}>()

// Type definition for filters
interface Filters {
  sort: { by: string; direction: 'asc' | 'desc' }
  visible: boolean // Filtre sur Autopsie === 0
  releves: string[]
  langues: string[]
  categories?: string[]
  thematiques?: string[]
  inedits?: boolean
  search: string
  type: 'all' | 'figure' | 'texte'
  mur: 'all' | 'Nord' | 'Sud' // Filtre sur la clé "Mur"
}

// Type definition for view mode
type ViewMode = 'card' | 'list' | 'table'

// Type pour le filtre mur
type MurFilter = 'all' | 'Nord' | 'Sud'

// --- State ---
const sortBy = ref('idBdc')
const sortDirection = ref<'asc' | 'desc'>('asc')
const typeFilter = ref<'all' | 'figure' | 'texte'>('all')
const murFilter = ref<MurFilter>('all')
const searchQuery = ref('')
const viewMode = ref<ViewMode>('card')
const showSortMenu = ref(false)

// State pour le dropdown
const showDropdown = ref(false)
const visibleState = ref(false)
const relevesState = ref<string[]>([])
const languesState = ref<string[]>([])
const categoriesState = ref<string[]>([])
const thematiqueState = ref<string[]>([])

// Listes des options disponibles
const categoriesList = ref<string[]>([])
const thematiquesList = ref<string[]>([])
const languesOptions = ref<string[]>([])

// Computed property pour filtrer les langues vides
const filteredLangues = computed(() => languesOptions.value.filter(l => l !== ''))

// Options de tri
const sortOptions = [
  { value: 'idBdc', label: 'Nom' },
  { value: 'Dim_lon', label: 'Longueur' },
  { value: 'Dim_lar', label: 'Largeur' },
]

// Computed pour le label du filtre mur
const wallFilterLabel = computed(() => {
  if (murFilter.value === 'Nord') return 'Mur Nord'
  if (murFilter.value === 'Sud') return 'Mur Sud'
  return 'Mur Nord et Sud'
})

const wallFilterIcon = computed(() => {
  if (murFilter.value === 'Nord') return ArrowRightToLine
  if (murFilter.value === 'Sud') return ArrowLeftToLine
  return ArrowRightLeft
})

// Chargement des données
onMounted(async () => {
  try {
    if (props.showCategoriesFilter) {
      categoriesList.value = await cacheService.getAllCategories()
    }
    if (props.showThematiqueFilter) {
      thematiquesList.value = await cacheService.getAllThemes()
    }
    // Chargement des langues
    languesOptions.value = await cacheService.getAllLanguages()
  } catch (error) {
    console.error('[BdC-GraffitiToolbar] Erreur lors du chargement des filtres:', error)
  }
})

// --- Computed ---
const typeFilterLabel = computed(() => {
  if (typeFilter.value === 'figure') return 'Figures'
  if (typeFilter.value === 'texte') return 'Textes'
  return 'Figures & Textes'
})

const typeFilterIcon = computed(() => {
  if (typeFilter.value === 'figure') return ImageIcon
  if (typeFilter.value === 'texte') return Type
  return Filter // Icône générique pour "Tous"
})

const hasActiveDropdownFilters = computed(() => {
  return (
    visibleState.value ||
    relevesState.value.length > 0 ||
    languesState.value.length > 0 ||
    categoriesState.value.length > 0 ||
    thematiqueState.value.length > 0
  )
})

const sortByLabel = computed(() => {
  const option = sortOptions.find(opt => opt.value === sortBy.value)
  return option ? option.label : 'Nom'
})

// --- Methods ---
const toggleSortMenu = () => {
  showSortMenu.value = !showSortMenu.value
}

const selectSortOption = (value: string) => {
  sortBy.value = value
  showSortMenu.value = false
  emitFilters()
}

const toggleSortDirection = (event: Event) => {
  event.stopPropagation()
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  emitFilters()
}

// Fermer le menu de tri quand on clique ailleurs
const closeSortMenu = (event: MouseEvent) => {
  if (showSortMenu.value && !(event.target as Element)?.closest('.relative')) {
    showSortMenu.value = false
  }
}

const cycleTypeFilter = () => {
  const sequence: Array<'all' | 'figure' | 'texte'> = ['all', 'figure', 'texte']
  const currentIndex = sequence.indexOf(typeFilter.value)
  typeFilter.value = sequence[(currentIndex + 1) % sequence.length]
  emitFilters()
}

const setViewMode = (mode: ViewMode) => {
  viewMode.value = mode
  emit('update:view', viewMode.value)
}

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
}

const closeDropdown = (event: MouseEvent) => {
  // Fermer si on clique en dehors du dropdown
  // Vérifier que l'élément cliqué n'est pas le bouton ou dans le dropdown
  if (showDropdown.value && !(event.target as Element)?.closest('.relative')) {
    showDropdown.value = false
  }
}

const resetDropdownFilters = () => {
  visibleState.value = false
  relevesState.value = []
  languesState.value = []
  categoriesState.value = []
  thematiqueState.value = []
  //console.log('[Toolbar] Reset des filtres');
  emitFilters()
}

const exportData = () => {
  //console.log('Export des graffitis');
  emit('export')
}

// Gestion des relevés
const toggleReleve = (year: string) => {
  const index = relevesState.value.indexOf(year)
  if (index === -1) {
    relevesState.value.push(year)
  } else {
    relevesState.value.splice(index, 1)
  }
  // Si les deux sont cochés ou aucun n'est coché, on réinitialise
  if (relevesState.value.length === 2 || relevesState.value.length === 0) {
    relevesState.value = []
  }
  //console.log('[Toolbar] État des relevés après toggle:', relevesState.value);
  emitFilters()
}

// Méthode pour cycler le filtre mur
const cycleWallFilter = () => {
  const sequence: MurFilter[] = ['all', 'Nord', 'Sud']
  const currentIndex = sequence.indexOf(murFilter.value)
  murFilter.value = sequence[(currentIndex + 1) % sequence.length]
  emitFilters()
}
// Emit des filtres mis à jour
const emitFilters = () => {
  const filters: Filters = {
    sort: { by: sortBy.value, direction: sortDirection.value },
    visible: visibleState.value,
    releves: relevesState.value,
    langues: languesState.value,
    search: searchQuery.value.trim(),
    type: typeFilter.value,
    mur: murFilter.value,
    inedits: false,
  }

  if (props.showCategoriesFilter && categoriesState.value.length > 0) {
    filters.categories = categoriesState.value
  }
  if (props.showThematiqueFilter && thematiqueState.value.length > 0) {
    filters.thematiques = thematiqueState.value
  }

  emit('update:filters', filters)
}

// Debounced version for search input
let debounceTimeout: ReturnType<typeof setTimeout> | null = null

const debouncedEmitFilters = () => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
  debounceTimeout = setTimeout(() => {
    emitFilters()
  }, 300)
}

// --- Lifecycle and Event Listeners for Dropdown ---
onMounted(() => {
  document.addEventListener('click', closeSortMenu)
  document.addEventListener('click', closeDropdown)
})
onUnmounted(() => {
  document.removeEventListener('click', closeSortMenu)
  document.removeEventListener('click', closeDropdown)
})
</script>

<style scoped>
/* Style pour la transition du dropdown */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
